import { useState, useRef, useEffect } from 'react';
import { Box, Flex, Input, Button, Text } from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { GoogleLogin } from '@react-oauth/google';
// import AppleSignin from 'react-apple-signin-auth';
// import type { AppleAuthResponse } from 'react-apple-signin-auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import { usePostHog } from '@posthog/react';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToSignup?: () => void;
  onSwitchToPasswordReset?: () => void;
}

const LAST_SOCIAL_KEY = '@last_social_provider';

const LoginForm = ({ onSuccess, onSwitchToSignup, onSwitchToPasswordReset }: LoginFormProps) => {
  const { t } = useTranslation();
  const { login, loginWithOAuth } = useAuth();
  const posthog = usePostHog();
  const [showPw, setShowPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(0);
  const lastSocial = localStorage.getItem(LAST_SOCIAL_KEY);

  useEffect(() => {
    const el = googleBtnRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setGoogleBtnWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(t('auth.emailRequired')).email(t('auth.emailInvalid')),
    password: Yup.string()
      .required(t('auth.passwordRequired'))
      .min(8, t('auth.passwordMinLength')),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      try {
        await login(values.email, values.password);
        posthog?.identify(values.email, { email: values.email });
        posthog?.capture('user_logged_in', { method: 'email' });
        onSuccess?.();
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || t('auth.loginFailed');
        setError(errorMessage);
        toast.error(errorMessage);
        posthog?.captureException(err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      if (credentialResponse.credential) {
        await loginWithOAuth('google', credentialResponse.credential);
        posthog?.capture('user_logged_in', { method: 'google' });
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || t('auth.googleLoginFailed');
      toast.error(errorMessage);
      posthog?.captureException(error);
    }
  };

  const handleGoogleError = () => {
    toast.error(t('auth.googleLoginFailed'));
  };

  // const handleAppleSuccess = async (response: AppleAuthResponse) => {
  //   try {
  //     const idToken = response.authorization.id_token;
  //     const userData = response.user
  //       ? {
  //           first_name: response.user.name?.firstName,
  //           last_name: response.user.name?.lastName,
  //         }
  //       : undefined;
  //     await loginWithOAuth('apple', idToken, userData);
  //     onSuccess?.();
  //   } catch (error: any) {
  //     const errorMessage = error.response?.data?.detail || 'Apple login failed. Please try again.';
  //     toast.error(errorMessage);
  //   }
  // };

  // const handleAppleError = (error: any) => {
  //   if (error?.error === 'popup_closed_by_user') return;
  //   toast.error('Apple login failed. Please try again.');
  // };

  return (
    <Box as="form" onSubmit={formik.handleSubmit as any}>
      <Flex direction="column" rowGap="24px">
        {/* Title */}
        <Flex justifyContent="center">
          <Box
            fontSize="26px"
            fontWeight="500"
            color="neutral.100"
          >
            {t('auth.login')}
          </Box>
        </Flex>

        {/* OAuth Buttons */}
        <Flex direction="column" gap="12px" width="100%">
        {/* Google Sign In */}
        <Flex direction="column">
          {lastSocial === 'google' && (
            <Flex justifyContent="flex-end" px="4px">
              <Box
                px="6px"
                py="1px"
                fontSize="10px"
                fontWeight="500"
                color="neutral.200"
                backgroundColor="#006D69"
                borderTopRadius="4px"
                borderBottomRadius="0"
                lineHeight="normal"
              >
                {t('auth.lastUsed')}
              </Box>
            </Flex>
          )}
          <Box
            ref={googleBtnRef}
            width="100%"
            borderRadius="4px"
            overflow="hidden"
            borderWidth={lastSocial === 'google' ? '1px' : '0px'}
            borderColor="#006D69"
          >
            {googleBtnWidth > 0 && (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                size="large"
                width={googleBtnWidth}
                text="continue_with"
              />
            )}
          </Box>
        </Flex>

        {/* Apple Sign In - commented out for now
        <Flex direction="column">
          {lastSocial === 'apple' && (
            <Flex justifyContent="flex-end" px="4px">
              <Box
                px="6px"
                py="1px"
                fontSize="10px"
                fontWeight="500"
                color="neutral.200"
                backgroundColor="#006D69"
                borderTopRadius="4px"
                borderBottomRadius="0"
                lineHeight="normal"
              >
                Last used
              </Box>
            </Flex>
          )}
          <Box
            borderRadius="4px"
            overflow="hidden"
            borderWidth={lastSocial === 'apple' ? '1px' : '0px'}
            borderColor="#006D69"
          >
        <AppleSignin
          authOptions={{
            clientId: import.meta.env.VITE_APPLE_CLIENT_ID || '',
            scope: 'email name',
            redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI || '',
            usePopup: true,
          }}
          onSuccess={handleAppleSuccess}
          onError={handleAppleError}
          uiType="dark"
          className="apple-signin-btn"
          noDefaultStyle={false}
          render={(props: Record<string, unknown>) => (
            <button
              {...props}
              type="button"
              style={{
                width: '100%',
                height: 40,
                backgroundColor: '#000',
                color: '#e8eaed',
                border: 'none',
                borderRadius: 0,
                fontSize: 14,
                fontFamily: "'Google Sans', arial, sans-serif",
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                letterSpacing: 0.25,
              }}
            >
              <span style={{ width: 40, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 814 1000">
                  <path
                    d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.3-81-102-208-102-329.8 0-194.3 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span style={{ flex: 1, textAlign: 'center' }}>Sign In with Apple</span>
              <span style={{ width: 6, flexShrink: 0 }} />
            </button>
          )}
        />
          </Box>
        </Flex>
        */}
        </Flex>

        {/* Divider */}
        <Flex alignItems="center" columnGap="18px">
          <Box
            color="neutral.400"
            borderBottomWidth="1px"
            borderColor="neutral.400"
            width="100%"
          />
          <Box
            color="neutral.400"
            fontSize="14px"
            fontWeight="600"
          >
            {t('common.or')}
          </Box>
          <Box
            color="neutral.400"
            borderBottomWidth="1px"
            borderColor="neutral.400"
            width="100%"
          />
        </Flex>

        {/* Email Field */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box
              fontWeight="400"
              fontSize="14px"
              lineHeight="150%"
              color="neutral.300"
            >
              {t('auth.email')}
            </Box>
            <Box
              textAlign="center"
              color="red"
              fontSize="14px"
              fontWeight="300"
            >
              {formik.touched.email && formik.errors.email ? formik.errors.email : null}
            </Box>
          </Flex>
          <Input
            name="email"
            type="email"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterEmail')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        {/* Password Field with Toggle */}
        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box
              fontWeight="400"
              fontSize="14px"
              lineHeight="150%"
              color="neutral.300"
            >
              {t('auth.password')}
            </Box>
            <Box
              textAlign="center"
              color="red"
              fontSize="14px"
              fontWeight="300"
            >
              {formik.touched.password && formik.errors.password ? formik.errors.password : null}
            </Box>
          </Flex>
          <Flex position="relative">
            <Input
              type={showPw ? 'text' : 'password'}
              name="password"
              p="10px"
              pr="40px"
              fontWeight="400"
              fontSize="14px"
              lineHeight="150%"
              placeholder={t('auth.enterPassword')}
              color="neutral.300"
              borderWidth="1px"
              borderColor="neutral.700"
              backgroundColor="neutral.800"
              borderRadius="8px"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <Button
              position="absolute"
              right="4px"
              top="50%"
              transform="translateY(-50%)"
              h='1.75rem'
              size='sm'
              fontSize="14px"
              fontWeight="600"
              backgroundColor="transparent"
              onClick={() => setShowPw(!showPw)}
              type="button"
            >
              {showPw ? <FaEye color="white" /> : <FaEyeSlash color="white" />}
            </Button>
          </Flex>
        </Flex>

        {/* Forgot Password Link */}
        {onSwitchToPasswordReset && (
          <Flex justifyContent="flex-end">
            <Text
              as="button"
              fontSize="14px"
              fontWeight="400"
              color="neutral.400"
              textDecoration="underline"
              cursor="pointer"
              bg="none"
              border="none"
              onClick={onSwitchToPasswordReset}
              _hover={{ color: 'neutral.300' }}
            >
              {t('auth.forgotPassword')}
            </Text>
          </Flex>
        )}

        {/* Submit Button */}
        <Flex alignItems="center">
          <Button
            type="submit"
            height="fit-content"
            width="100%"
            py="8px"
            fontWeight="500"
            fontSize="14px"
            color="neutral.900"
            backgroundColor="neutral.100"
            loading={isLoading}
            disabled={isLoading || !formik.values.email || !formik.values.password || !formik.isValid}
          >
            {t('common.continue')}
          </Button>
        </Flex>

        {/* Error Message */}
        {error && (
          <Box
            mt="8px"
            textAlign="center"
            color="red"
            fontSize="14px"
            fontWeight="400"
          >
            {error}
          </Box>
        )}

        {/* Switch to Signup */}
        {onSwitchToSignup && (
          <Flex justifyContent="center" alignItems="center" columnGap="4px">
            <Box fontSize="14px" fontWeight="400" color="neutral.400">
              {t('auth.noAccount')}
            </Box>
            <Text
              as="button"
              fontSize="14px"
              fontWeight="500"
              color="neutral.200"
              textDecoration="underline"
              cursor="pointer"
              bg="none"
              border="none"
              onClick={onSwitchToSignup}
              _hover={{ color: 'neutral.100' }}
            >
              {t('auth.signup')}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default LoginForm;
